import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addBannerTypes,
  addLogoUrlTypes,
  addSupplierLogoTypes,
  editFooterTypes,
  editMetadataResponseTypes,
  editSupplierTypes,
  footerTypes,
  idResponseTypes,
  initialFooterTypes,
  PublicationBannersTypes,
  PublicationContactTypes,
  PublicationPoliciesTypes,
  PublicationTheme,
  PublicationTranslationsTypes,
  publicationTypes,
  requestPublicationTypes,
  supplierTypes,
  userRoleTypes,
  UsersCollection,
} from "./adminTypes";
import { editMetadataTypes } from "../articles/articlesTypes";
import { SuccessType } from "../commonTypes";
import { ApiError, toApiError, UNKNOWN_ERROR } from "../apiError";
import adminService from "./adminService";

interface adminState {
  publicationTheme: PublicationTheme | null;
  // Momento (ms) en que se guardó publicationTheme; el store lo usa al
  // rehidratar para descartar un tema persistido demasiado antiguo.
  publicationThemeFetchedAt: number | null;
  publications: publicationTypes[];
  editedPublication: publicationTypes | null;
  supplier: supplierTypes | null;
  suppliers: supplierTypes[];
  loading: boolean;
  loadingUsers: boolean;
  magazineLoading: boolean;
  magazineThemeLoading: boolean;
  error: ApiError | null;
  themeError: ApiError | null;
  users: userRoleTypes[] | UsersCollection | null;
  initialFooter: initialFooterTypes | null;
  publicationFooter: footerTypes | null;
  publicationContact: PublicationContactTypes | null;
  publicationBanners: PublicationBannersTypes | null;
  footerLoading: boolean;
  contactLoading: boolean;
  bannersLoading: boolean;
  publicationTranslations: PublicationTranslationsTypes | null;
  // Flags independientes para que acciones no relacionadas no se pisen entre sí
  reparseLoading: boolean;
  supplierLoading: boolean;
  publicationSaving: boolean;
}

const initialState: adminState = {
  publicationTheme: null,
  publicationThemeFetchedAt: null,
  publications: [],
  editedPublication: null,
  supplier: null,
  suppliers: [],
  magazineLoading: false,
  magazineThemeLoading: false,
  loading: false,
  loadingUsers: false,
  error: null,
  themeError: null,
  users: null,
  initialFooter: null,
  publicationFooter: null,
  publicationContact: null,
  publicationBanners: null,
  footerLoading: false,
  contactLoading: false,
  bannersLoading: false,
  publicationTranslations: null,
  reparseLoading: false,
  supplierLoading: false,
  publicationSaving: false,
};

export const getPublications = createAsyncThunk<
  publicationTypes[],
  void,
  { rejectValue: ApiError }
>("admin/getPublications", async (_, { rejectWithValue }) => {
  try {
    const res = await adminService.getPublications();
    return res;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getPublicationBySlug = createAsyncThunk<
  publicationTypes,
  string,
  { rejectValue: ApiError }
>("admin/getPublicationBySlug", async (slug, { rejectWithValue }) => {
  try {
    const res = await adminService.getPublicationBySlug(slug);
    return res;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getPublicationStyle = createAsyncThunk<
  PublicationTheme,
  void,
  { rejectValue: ApiError }
>("admin/getPublicationStyle", async (_, { rejectWithValue }) => {
  try {
    const res = await adminService.getPublicationStyle();
    return res;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const createPublication = createAsyncThunk<
  SuccessType,
  requestPublicationTypes,
  { rejectValue: ApiError }
>(
  "admin/createPublication",
  async (data: requestPublicationTypes, { rejectWithValue }) => {
    try {
      const response = await adminService.createPublication(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const editPublication = createAsyncThunk<
  any,
  any,
  { rejectValue: ApiError }
>("admin/editPublication", async (data: any, { rejectWithValue }) => {
  try {
    const response = await adminService.editPublication(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const deletePublication = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: ApiError }
>("admin/deletePublication", async (slug, { rejectWithValue }) => {
  try {
    const res = await adminService.deletePublication(slug);
    return res;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const createArticle = createAsyncThunk<
  string,
  FormData,
  { rejectValue: ApiError }
>("admin/createArticle", async (data: FormData, { rejectWithValue }) => {
  try {
    const response = await adminService.createArticle(data);

    return response;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getSuppliers = createAsyncThunk<
  supplierTypes[],
  void,
  { rejectValue: ApiError }
>("admin/getSuppliers", async (_, { rejectWithValue }) => {
  try {
    const res = await adminService.getSuppliers();
    return res;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getSupplier = createAsyncThunk<
  supplierTypes,
  string,
  { rejectValue: ApiError }
>("admin/getSupplier", async (supplier_name, { rejectWithValue }) => {
  try {
    const res = await adminService.getSupplier(supplier_name);
    return res;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const editSupplier = createAsyncThunk<
  SuccessType,
  editSupplierTypes,
  { rejectValue: ApiError }
>(
  "admin/editSupplier",
  async (data: editSupplierTypes, { rejectWithValue }) => {
    try {
      const response = await adminService.editSupplier(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const deleteSupplier = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: ApiError }
>("admin/deleteSupplier", async (name, { rejectWithValue }) => {
  try {
    const res = await adminService.deleteSupplier(name);
    return res;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const editMetadata = createAsyncThunk<
  editMetadataResponseTypes,
  editMetadataTypes,
  { rejectValue: ApiError }
>("admin/editMetadata", async (editMetadataRequest, { rejectWithValue }) => {
  try {
    const response = await adminService.editMetadata(editMetadataRequest);
    return response;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const addLogoUrl = createAsyncThunk<
  publicationTypes,
  addLogoUrlTypes,
  { rejectValue: ApiError }
>("admin/addLogoUrl", async (data: addLogoUrlTypes, { rejectWithValue }) => {
  try {
    const response = await adminService.addLogoUrl(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const addFavicon = createAsyncThunk<
  SuccessType,
  addLogoUrlTypes,
  { rejectValue: ApiError }
>("admin/addFavicon", async (data: addLogoUrlTypes, { rejectWithValue }) => {
  try {
    const response = await adminService.addFavicon(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const addSupplierLogo = createAsyncThunk<
  SuccessType,
  addSupplierLogoTypes,
  { rejectValue: ApiError }
>(
  "admin/addSupplierLogo",
  async (addSupplierLogoRequest: addSupplierLogoTypes, { rejectWithValue }) => {
    try {
      const response = await adminService.addSupplierLogo(
        addSupplierLogoRequest,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const addBanner = createAsyncThunk<
  SuccessType,
  any,
  { rejectValue: ApiError }
>("admin/addBanner", async (addBannerRequest: any, { rejectWithValue }) => {
  try {
    const response = await adminService.addBanner(addBannerRequest);
    return response;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const addBannerImg = createAsyncThunk<
  string,
  addBannerTypes,
  { rejectValue: ApiError }
>(
  "admin/addBannerImg",
  async (addBannerImgRequest: addBannerTypes, { rejectWithValue }) => {
    try {
      const response = await adminService.addBannerImg(addBannerImgRequest);
      return response;
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const getRoles = createAsyncThunk<
  userRoleTypes[],
  void,
  { rejectValue: ApiError }
>("admin/getRoles", async (_, { rejectWithValue }) => {
  try {
    const response = await adminService.getRoles();
    return response;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getRolesByUser = createAsyncThunk<
  UsersCollection,
  void,
  { rejectValue: ApiError }
>("admin/getRolesByUser", async (_, { rejectWithValue }) => {
  try {
    const response = await adminService.getRolesByUser();
    return response;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getNonStaffRolesByUser = createAsyncThunk<
  UsersCollection,
  void,
  { rejectValue: ApiError }
>("admin/getNonStaffRolesByUser", async (_, { rejectWithValue }) => {
  try {
    const response = await adminService.getNonStaffRolesByUser();
    return response;
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getOrCreateSupplier = createAsyncThunk<
  supplierTypes,
  string,
  { rejectValue: ApiError }
>("admin/getOrCreateSupplier", async (supplier_name, thunkAPI) => {
  try {
    const res = await adminService.getOrCreateSupplier(supplier_name);
    return res;
  } catch (error: any) {
    console.error("Error en getOrCreateSupplier:", error);
    return thunkAPI.rejectWithValue(toApiError(error));
  }
});

export const getPublicationFooter = createAsyncThunk<
  PublicationPoliciesTypes,
  void,
  { rejectValue: ApiError }
>("admin/getPublicationFooter", async (_, { rejectWithValue }) => {
  try {
    return await adminService.getPublicationFooter();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getInitialFooter = createAsyncThunk<
  initialFooterTypes,
  void,
  { rejectValue: ApiError }
>("admin/getInitialFooter", async (_, { rejectWithValue }) => {
  try {
    return await adminService.getInitialFooter();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getPublicationContact = createAsyncThunk<
  PublicationContactTypes,
  void,
  { rejectValue: ApiError }
>("admin/getPublicationContact", async (_, { rejectWithValue }) => {
  try {
    return await adminService.getPublicationContact();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getPublicationBanners = createAsyncThunk<
  PublicationBannersTypes,
  void,
  { rejectValue: ApiError }
>("admin/getPublicationBanners", async (_, { rejectWithValue }) => {
  try {
    return await adminService.getPublicationBanners();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const editPublicationFooter = createAsyncThunk<
  idResponseTypes,
  editFooterTypes,
  { rejectValue: ApiError }
>(
  "admin/editPublicationFooter",
  async (editFooterRequest: editFooterTypes, { rejectWithValue }) => {
    try {
      return await adminService.editPublicationFooter(editFooterRequest);
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const reparseWord = createAsyncThunk<
  string,
  { article_id: number; url: string },
  { rejectValue: ApiError }
>("admin/reparseWord", async ({ article_id, url }, { rejectWithValue }) => {
  try {
    return await adminService.reparseWord(article_id, url);
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const uploadHowToReviewFile = createAsyncThunk<
  SuccessType,
  FormData,
  { rejectValue: ApiError }
>("admin/uploadHowToReviewFile", async (formData, { rejectWithValue }) => {
  try {
    return await adminService.uploadHowToReviewFile(formData);
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const deleteHowToReviewFile = createAsyncThunk<
  SuccessType,
  void,
  { rejectValue: ApiError }
>("admin/deleteHowToReviewFile", async (_, { rejectWithValue }) => {
  try {
    return await adminService.deleteHowToReviewFile();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const getPublicationTranslations = createAsyncThunk<
  PublicationTranslationsTypes,
  void,
  { rejectValue: ApiError }
>("admin/getPublicationTranslations", async (_, { rejectWithValue }) => {
  try {
    return await adminService.getPublicationTranslations();
  } catch (error: any) {
    return rejectWithValue(toApiError(error));
  }
});

export const editOrCreatePublicationTranslations = createAsyncThunk<
  SuccessType,
  PublicationTranslationsTypes,
  { rejectValue: ApiError }
>(
  "admin/editOrCreatePublicationTranslations",
  async (translationsData, { rejectWithValue }) => {
    try {
      return await adminService.editOrCreatePublicationTranslations(
        translationsData,
      );
    } catch (error: any) {
      return rejectWithValue(toApiError(error));
    }
  },
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    resetPublication: (state) => {
      state.publicationTheme = null;
      state.publicationThemeFetchedAt = null;
      state.publications = [];
      state.editedPublication = null;
      state.supplier = null;
      state.suppliers = [];
      state.users = null;
      state.publicationContact = null;
      state.publicationBanners = null;
      state.themeError = null;
    },
  },
  extraReducers: (builder) => {
    // --- Publications ---
    builder.addCase(getPublications.fulfilled, (state, action) => {
      state.publications = action.payload;
    });

    // --- Publication Detail / Theme ---
    builder.addCase(getPublicationBySlug.fulfilled, (state, action) => {
      state.editedPublication = action.payload;
    });
    // themeError es el único error que invalida la app (ver useClient); el
    // resto de fallos de admin quedan solo en state.error.
    builder.addCase(getPublicationStyle.pending, (state) => {
      state.themeError = null;
    });
    builder.addCase(getPublicationStyle.fulfilled, (state, action) => {
      state.publicationTheme = action.payload;
      state.publicationThemeFetchedAt = Date.now();
      state.themeError = null;
    });
    builder.addCase(getPublicationStyle.rejected, (state, action) => {
      state.themeError = action.payload ?? UNKNOWN_ERROR;
    });

    // --- Suppliers ---
    builder.addCase(getSuppliers.fulfilled, (state, action) => {
      state.suppliers = action.payload;
    });
    builder.addCase(getSupplier.pending, (state) => {
      state.supplier = null;
      state.error = null;
    });
    builder.addCase(getSupplier.fulfilled, (state, action) => {
      state.supplier = action.payload;
    });
    builder.addCase(getOrCreateSupplier.fulfilled, (state, action) => {
      state.supplier = action.payload;
    });

    // --- Roles / Users ---
    builder.addCase(getRoles.pending, (state) => {
      state.users = null;
    });
    builder.addCase(getRoles.fulfilled, (state, action) => {
      state.users = action.payload;
    });
    builder
      .addCase(getRolesByUser.fulfilled, (state, action) => {
        state.users = action.payload;
        state.loadingUsers = false;
        state.error = null;
      })
      .addCase(getRolesByUser.pending, (state) => {
        state.users = null;
        state.loadingUsers = true;
        state.error = null;
      })
      .addCase(getRolesByUser.rejected, (state, action) => {
        state.users = null;
        state.loadingUsers = false;
        state.error = action.payload ?? UNKNOWN_ERROR;
      });
    builder
      .addCase(getNonStaffRolesByUser.fulfilled, (state, action) => {
        state.users = action.payload;
        state.loadingUsers = false;
        state.error = null;
      })
      .addCase(getNonStaffRolesByUser.pending, (state) => {
        state.users = null;
        state.loadingUsers = true;
        state.error = null;
      })
      .addCase(getNonStaffRolesByUser.rejected, (state, action) => {
        state.users = null;
        state.loadingUsers = false;
        state.error = action.payload ?? UNKNOWN_ERROR;
      });
    builder.addCase(getPublicationFooter.fulfilled, (state, action) => {
      state.publicationFooter = action.payload;
    });
    builder.addCase(getInitialFooter.fulfilled, (state, action) => {
      state.initialFooter = action.payload;
    });
    builder.addCase(getPublicationContact.fulfilled, (state, action) => {
      state.publicationContact = action.payload;
    });
    builder.addCase(getPublicationBanners.fulfilled, (state, action) => {
      state.publicationBanners = action.payload;
    });
    builder.addCase(reparseWord.fulfilled, (state) => {
      state.error = null;
      state.reparseLoading = false;
    });
    builder.addCase(reparseWord.rejected, (state, action) => {
      state.error = action.payload ?? UNKNOWN_ERROR;
      state.reparseLoading = false;
    });
    builder.addCase(reparseWord.pending, (state) => {
      state.reparseLoading = true;
      state.error = null;
    });
    builder.addCase(uploadHowToReviewFile.fulfilled, (state) => {
      state.error = null;
    });
    builder.addCase(uploadHowToReviewFile.rejected, (state, action) => {
      state.error = action.payload ?? UNKNOWN_ERROR;
    });
    builder.addCase(uploadHowToReviewFile.pending, (state) => {
      state.error = null;
    });
    builder.addCase(deleteHowToReviewFile.fulfilled, (state) => {
      state.error = null;
    });
    builder.addCase(deleteHowToReviewFile.rejected, (state, action) => {
      state.error = action.payload ?? UNKNOWN_ERROR;
    });
    builder.addCase(deleteHowToReviewFile.pending, (state) => {
      state.error = null;
    });
    builder.addCase(editPublication.fulfilled, (state, action) => {
      state.publicationTheme = action.payload;
      state.publicationThemeFetchedAt = Date.now();
      state.error = null;
    });
    builder.addCase(getPublicationTranslations.fulfilled, (state, action) => {
      state.error = null;
      state.publicationTranslations = action.payload;
    });
    builder.addCase(getPublicationTranslations.rejected, (state, action) => {
      state.error = action.payload ?? UNKNOWN_ERROR;
    });
    builder.addCase(
      editOrCreatePublicationTranslations.fulfilled,
      (state, action) => {
        state.error = null;
        // Mantener las traducciones en memoria al día con lo guardado
        if (state.publicationTranslations) {
          for (const [lang, data] of Object.entries(action.meta.arg)) {
            state.publicationTranslations[lang] = {
              ...state.publicationTranslations[lang],
              ...data,
            };
          }
        }
      },
    );
    builder.addCase(
      editOrCreatePublicationTranslations.rejected,
      (state, action) => {
        state.error = action.payload ?? UNKNOWN_ERROR;
      },
    );

    type LoadingFlag = "loading" | "supplierLoading" | "publicationSaving";
    const trackLoading = (
      thunks: {
        pending: { type: string };
        fulfilled: { type: string };
        rejected: { type: string };
      }[],
      flag: LoadingFlag,
    ) => {
      builder.addMatcher(
        (action) => thunks.some((thunk) => action.type === thunk.pending.type),
        (state) => {
          state[flag] = true;
          state.error = null;
        },
      );
      builder.addMatcher(
        (action) =>
          thunks.some((thunk) =>
            [thunk.fulfilled.type, thunk.rejected.type].includes(action.type),
          ),
        (state) => {
          state[flag] = false;
        },
      );
    };

    // Acciones genéricas de admin sin un flag propio
    trackLoading(
      [
        getPublications,
        createPublication,
        deletePublication,
        createArticle,
        getSuppliers,
        deleteSupplier,
        addLogoUrl,
        addFavicon,
        addSupplierLogo,
        getRoles,
        getRolesByUser,
        editMetadata,
        addBanner,
        addBannerImg,
      ],
      "loading",
    );
    trackLoading(
      [getSupplier, getOrCreateSupplier, editSupplier],
      "supplierLoading",
    );
    trackLoading([editPublication, editPublicationFooter], "publicationSaving");

    const magazineLoadingActions = [getPublicationBySlug, getPublicationStyle];
    builder.addMatcher(
      (action) =>
        magazineLoadingActions.some((act) => action.type === act.pending.type),
      (state) => {
        state.magazineLoading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      (action) =>
        magazineLoadingActions.some((act) =>
          [act.fulfilled.type, act.rejected.type].includes(action.type),
        ),
      (state) => {
        state.magazineLoading = false;
      },
    );
    builder.addMatcher(
      (action) => action.type === getPublicationFooter.pending.type,
      (state) => {
        state.footerLoading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      (action) =>
        [
          getPublicationFooter.fulfilled.type,
          getPublicationFooter.rejected.type,
        ].includes(action.type),
      (state) => {
        state.footerLoading = false;
      },
    );
    builder.addMatcher(
      (action) => action.type === getPublicationContact.pending.type,
      (state) => {
        state.contactLoading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      (action) =>
        [
          getPublicationContact.fulfilled.type,
          getPublicationContact.rejected.type,
        ].includes(action.type),
      (state) => {
        state.contactLoading = false;
      },
    );
    builder.addMatcher(
      (action) => action.type === getPublicationBanners.pending.type,
      (state) => {
        state.bannersLoading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      (action) =>
        [
          getPublicationBanners.fulfilled.type,
          getPublicationBanners.rejected.type,
        ].includes(action.type),
      (state) => {
        state.bannersLoading = false;
      },
    );
    builder.addMatcher(
      (action) =>
        action.type.startsWith("admin/") && action.type.endsWith("/rejected"),
      (state, action: PayloadAction<ApiError | undefined>) => {
        state.error = action.payload ?? UNKNOWN_ERROR;
      },
    );
  },
});
export const { resetPublication } = adminSlice.actions;
export default adminSlice.reducer;
